import { useTranslation } from 'react-i18next';
import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import type { QltUploadFile } from '../../api/repository/upload';
import { Toast } from '../../components';
import { formatBytes } from '../../config/file-size';
import { FileTextIcon, MicIcon, VideoIcon } from '../../layout/icons';
import { useQltUploadStore } from '../../stores/qlt';
import { classifyUpload, isSupportedUpload, MAX_UPLOAD_SIZE_LABEL, uploadAccept, uploadKindSpec, uploadRejection, type UploadKind } from '../upload/validation';
import { CloudUploadIcon, PhotoPlaceholderIcon } from './panelGlyphs';
import { useMsg, type Msg } from '../../i18n/msg';

const TABS: Array<{ id: UploadKind; labelKey: 'upload.tab.image' | 'upload.tab.video' | 'upload.tab.audio' | 'upload.tab.document'; nounKey: 'upload.noun.image' | 'upload.noun.video' | 'upload.noun.audio' | 'upload.noun.document' }> = [
  { id: 'image', labelKey: 'upload.tab.image', nounKey: 'upload.noun.image' },
  { id: 'video', labelKey: 'upload.tab.video', nounKey: 'upload.noun.video' },
  { id: 'audio', labelKey: 'upload.tab.audio', nounKey: 'upload.noun.audio' },
  { id: 'document', labelKey: 'upload.tab.document', nounKey: 'upload.noun.document' },
];

function tabGlyph(kind: UploadKind, size: number): ReactNode {
  if (kind === 'video') return <VideoIcon size={size} />;
  if (kind === 'audio') return <MicIcon size={size} />;
  if (kind === 'document') return <FileTextIcon size={size} />;
  return <PhotoPlaceholderIcon size={size} />;
}

export function UploadPanel({ onOpenUpload }: { onOpenUpload: (file: QltUploadFile) => void }) {
  const { t } = useTranslation('panels');
  const tr = useMsg();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [tab, setTab] = useState<UploadKind>('image');
  const [validationError, setValidationError] = useState<Msg | null>(null);
  // Which tile is on the canvas - the store keeps no selection, this is presentation only.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const files = useQltUploadStore((state) => state.files);
  const uploading = useQltUploadStore((state) => state.uploading);
  const error = useQltUploadStore((state) => state.error);
  const upload = useQltUploadStore((state) => state.upload);
  const clearError = useQltUploadStore((state) => state.clearError);

  async function choose(picked: FileList | null) {
    const selected = picked ? Array.from(picked) : [];
    const invalid = selected.filter((file) => !isSupportedUpload(file, tab));
    if (invalid.length > 0) {
      setValidationError(uploadRejection(invalid[0], tab));
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    setValidationError(null);
    if (selected.length > 0) await upload(selected);
    if (inputRef.current) inputRef.current.value = '';
  }

  function focusTab(index: number) {
    const next = (index + TABS.length) % TABS.length;
    setTab(TABS[next].id);
    const node = tabRefs.current[next];
    if (node) node.focus();
  }

  function onTabKey(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    if (event.key === 'ArrowRight') { event.preventDefault(); focusTab(index + 1); }
    else if (event.key === 'ArrowLeft') { event.preventDefault(); focusTab(index - 1); }
    else if (event.key === 'Home') { event.preventDefault(); focusTab(0); }
    else if (event.key === 'End') { event.preventDefault(); focusTab(TABS.length - 1); }
  }

  const active = TABS.filter((item) => item.id === tab)[0];
  const shown = files.filter((file) => classifyUpload(file) === tab);
  const noun = t(active.nounKey);
  const hint = t('upload.hint', { noun, formats: uploadKindSpec(tab).formats, size: MAX_UPLOAD_SIZE_LABEL });

  function open(file: QltUploadFile) {
    setSelectedId(file.id);
    onOpenUpload(file);
  }

  return (
    <>
      <div className="up-pane">
        <input ref={inputRef} className="up-pane__input" type="file" accept={uploadAccept(tab)} multiple={true} tabIndex={-1} aria-hidden={true} onChange={(event) => void choose(event.target.files)} />
        <button type="button" className="up-pane__pick" disabled={uploading} onClick={() => inputRef.current?.click()}>
          <CloudUploadIcon size={15} />
          <span>{uploading ? t('upload.uploading') : t('upload.pick')}</span>
        </button>
        <p className="up-pane__status" role="status">{uploading ? t('upload.uploadingStatus') : ''}</p>
        {validationError ? <p className="up-pane__error" role="alert">{tr(validationError)}</p> : null}

        <div className="up-tabs" role="tablist" aria-label={t('upload.tabsLabel')}>
          {TABS.map((item, index) => (
            <button
              type="button"
              key={item.id}
              ref={(node) => { tabRefs.current[index] = node; }}
              role="tab"
              id={`upload-tab-${item.id}`}
              aria-selected={tab === item.id}
              aria-controls="upload-tabpanel"
              tabIndex={tab === item.id ? 0 : -1}
              className={tab === item.id ? 'is-active' : ''}
              onKeyDown={(event) => onTabKey(event, index)}
              onClick={() => { setTab(item.id); setValidationError(null); }}
            >
              {t(item.labelKey)}
            </button>
          ))}
        </div>

        <div className="up-pane__content" role="tabpanel" id="upload-tabpanel" aria-labelledby={`upload-tab-${tab}`}>
          {shown.length > 0 && tab === 'image' ? (
            <div className="up-grid">
              {shown.map((image) => {
                const selected = image.id === selectedId;
                return (
                  <button
                    type="button"
                    key={image.id}
                    className={selected ? 'up-grid__tile is-selected' : 'up-grid__tile'}
                    aria-current={selected ? 'true' : undefined}
                    aria-label={image.name}
                    title={image.name}
                    onClick={() => open(image)}
                  >
                    <img src={image.formats?.thumbnail?.url || image.url} alt="" />
                  </button>
                );
              })}
            </div>
          ) : null}
          {shown.length > 0 && tab !== 'image' ? (
            <ul className="up-list">
              {shown.map((file) => {
                const selected = file.id === selectedId;
                return (
                  <li key={file.id}>
                    <button type="button" className={selected ? 'up-list__row is-selected' : 'up-list__row'} aria-current={selected ? 'true' : undefined} title={file.name} onClick={() => open(file)}>
                      <span className="up-list__mark" aria-hidden={true}>{tabGlyph(tab, 18)}</span>
                      <span className="up-list__text"><span className="up-list__name">{file.name}</span><span className="up-list__meta">{formatBytes(file.size)}</span></span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
          {shown.length === 0 ? (
            <div className="up-empty">
              <div className="up-empty__mark" aria-hidden={true}>{tabGlyph(tab, 23)}</div>
              <p className="up-empty__title">{t('upload.empty', { noun })}</p>
              <p className="up-empty__hint">{hint}</p>
            </div>
          ) : null}
        </div>
      </div>
      <Toast open={Boolean(error)} kind="error" message={error || ''} duration={0} onClose={clearError} />
    </>
  );
}
